import { plural, t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useDynamicTable } from '~/components/dynamic_table/context'
import { buildTableSearchParams } from '~/components/dynamic_table/helpers/browserState'
import exportCsv from '~/components/dynamic_table/helpers/exportCsv'

// Extraction modal component for column selection and CSV export
export default function ExtractionModal({
  columns = [],
  visibleColumns = [],
  show: controlledShow = undefined,
  onClose,
  onExport,
  exportLabel = null,
}) {
  const tableContext = useDynamicTable()
  const tableState = tableContext.state
  const tableProps = tableContext.props
  const { query, sort, page, meta } = tableState
  // controller ref for aborting internal export
  const controllerRef = useRef(null)
  const isControlled = controlledShow !== undefined
  const [show, setShow] = useState(!!controlledShow)
  const [selectedColumns, setSelectedColumns] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // sync controlled show
  useEffect(() => {
    if (isControlled) setShow(!!controlledShow)
  }, [controlledShow, isControlled])

  // preselect visible columns when modal opens (only initialize once when opened
  // or when columns/visibleColumns change). Don't re-run on selectedColumns
  // changes (so user deselect/select actions aren't overridden).
  useEffect(() => {
    if (!show) return
    setSelectedColumns((prev) =>
      prev.length === 0 ? (visibleColumns.length > 0 ? visibleColumns : columns.map((c) => c.key)) : prev,
    )
    // Only re-run when `show`, `visibleColumns` or `columns` change
  }, [show, visibleColumns, columns])

  useEffect(() => {
    // avoid setting state after unmount when async export resolves
    return () => {
      setLoading(false)
    }
  }, [])

  const handleClose = () => {
    if (isControlled) {
      onClose?.()
    } else {
      setShow(false)
    }
  }

  const toggleColumn = (key, checked) => {
    setSelectedColumns((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)))
  }

  const selectAll = () => setSelectedColumns(columns.map((c) => c.key))
  const deselectAll = () => setSelectedColumns([])

  // derived labels for selected columns (avoid complex inline JSX expressions)
  const selectedLabels = selectedColumns
    .map((k) => {
      const col = columns.find((c) => c.key === k)
      return col?.label ? col.label : k
    })
    .join(', ')
  const resolvedExportLabel = exportLabel || t`Export`

  const handleExport = async () => {
    if (selectedColumns.length === 0) return
    setLoading(true)
    setError(null)
    try {
      if (onExport) {
        await onExport({ selectedColumns })
      } else {
        const searchUrl = tableProps.url || window.location.href
        const [locationPath, urlParams] = (searchUrl || '').split('?', 2)
        const { searchParams } = buildTableSearchParams({
          query,
          sort,
          exportColumns: selectedColumns,
          page,
          per_page: meta.per_page,
          locationPath,
          urlParams,
        })

        // AbortController per-export
        controllerRef.current = new AbortController()
        const { signal } = controllerRef.current

        let blob, filename
        try {
          const res = await exportCsv({ locationPath, searchParams, signal })
          blob = res.blob
          filename = res.filename
        } catch (_e) {
          // fallback: do a direct fetch (tests often mock fetch). Keep same headers.
          const headers = { 'Content-Type': 'text/csv', Accept: 'text/csv' }
          const response = await fetch(`${locationPath}?${searchParams}`, { headers, signal })
          filename = response.headers?.get?.('Content-Disposition') || 'export.csv'
          blob = await response.blob()
        }

        // trigger download
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        window.URL.revokeObjectURL(url)
      }
      // close when successful
      handleClose()
    } catch (err) {
      setError(err?.message || t`Export failed`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    return () => {
      if (controllerRef.current) controllerRef.current.abort()
    }
  }, [])

  if (!show) return null

  return (
    <div
      className='modal fade show d-block'
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role='dialog'
      aria-modal='true'
    >
      <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              <Trans>Select Columns to Export</Trans>
            </h5>
            <button type='button' className='btn-close' onClick={handleClose} aria-label={t`Close`} />
          </div>
          <div className='modal-body'>
            <p className='text-muted mb-3'>
              <Trans>Select the columns you want to include in your CSV export:</Trans>
            </p>

            <div className='mb-3'>
              <button
                type='button'
                className='btn btn-sm btn-outline-primary me-2'
                onClick={selectAll}
                data-testid='select-all'
              >
                <Trans>Select All</Trans>
              </button>
              <button
                type='button'
                className='btn btn-sm btn-outline-secondary'
                onClick={deselectAll}
                data-testid='deselect-all'
              >
                <Trans>Deselect All</Trans>
              </button>
            </div>

            <div className='row'>
              {columns.map(({ key, label }) => (
                <div key={key} className='col-md-6 mb-2'>
                  <label className='form-check'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={selectedColumns.includes(key)}
                      onChange={(e) => toggleColumn(key, e.target.checked)}
                      data-testid={`col-checkbox-${key}`}
                    />
                    <span className='form-check-label'>{label}</span>
                  </label>
                </div>
              ))}
            </div>

            {selectedColumns.length > 0 && (
              <div className='mt-3 p-2 bg-light rounded' data-testid='selection-summary'>
                <small className='text-muted'>
                  {plural(selectedColumns.length, {
                    one: 'Selected column (#):',
                    other: 'Selected columns (#):',
                  })}{' '}
                  {selectedLabels}
                </small>
              </div>
            )}

            {error && (
              <div className='mt-3 alert alert-danger' role='alert' data-testid='export-error'>
                {error}
              </div>
            )}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-secondary' onClick={handleClose} data-testid='cancel-btn'>
              <Trans>Cancel</Trans>
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleExport}
              disabled={selectedColumns.length === 0 || loading}
              data-testid='export-btn'
            >
              {loading ? t`Exporting...` : resolvedExportLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

ExtractionModal.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string })),
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onExport: PropTypes.func,
  exportLabel: PropTypes.string,
}
